import type { Tag } from '@/features/email-flows/types/audience.types';

// Flow status constants
export const FLOW_STATUS = {
    DELETED: 'deleted',
    DRAFT: 'draft',
    PUBLISHED: 'published',
} as const;

export type FlowStatus = (typeof FLOW_STATUS)[keyof typeof FLOW_STATUS];

// Flow type constants
export const FLOW_TYPE = {
    EVENT_TRIGGERED: 'event-triggered',
    USER_TRIGGERED: 'user-triggered',
} as const;

export type FlowType = (typeof FLOW_TYPE)[keyof typeof FLOW_TYPE];

// Flow trigger constants
export const FLOW_TRIGGER = {
    CHECKOUT: 'checkout',
    TIME: 'time',
    CART_ABANDONMENT: 'cart-abandonment',
} as const;

export type FlowTrigger = (typeof FLOW_TRIGGER)[keyof typeof FLOW_TRIGGER];

// Address payload for onboarding
export interface AddressPayload {
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
}

// Flow email structure
export interface FlowEmail {
    flow_email_id: number;
    flow_id: number;
    subject?: string;
    content?: string;
    status?: FlowStatus;
    delay?: number;
    data?: {
        subject?: string;
        body?: string;
        delay?: number;
    };
    analytics?: {
        sent?: number;
        opened?: number;
        clicked?: number;
        revenue?: number;
    };
}

// Flow analytics structure
export interface FlowAnalytics {
    sent?: number;
    opened?: number;
    clicked?: number;
    revenue?: number;
    sales?: number;
}

// Flow analytics API response structure
export interface FlowAnalyticsApiResponse {
    analytics: Record<
        number,
        {
            analytics: FlowAnalytics;
            emails: Record<number, FlowAnalytics>;
        }
    >;
    has_more: boolean;
}

// Main flow summary structure
export interface FlowSummary {
    flow_id: number;
    type: FlowType;
    trigger?: FlowTrigger;
    trigger_type?: FlowTrigger;
    status: FlowStatus;
    emails_count?: number;
    recipient_count?: number;
    created_at?: string;
    scheduled_timestamp?: number;
    sent_timestamp?: number;
    flow_emails?: FlowEmail[];
    analytics?: FlowAnalytics;
    data?: {
        name?: string;
        recipient_count?: number;
        audience_list?: number[];
        started?: boolean;
        started_at?: number;
        specific_time?: {
            tags?: Tag[];
            date?: string;
            time?: string;
            timezone?: string;
        };
        filters?: {
            page_id_list?: number[];
            exclude_page_id_list?: number[];
        };
    };
    filters?: {
        page_id_list?: number[];
    };
}

// API response structures
export interface EmailFlowsResponse {
    eventTriggered: FlowSummary[];
}

export interface BroadcastsResponse {
    userTriggered: FlowSummary[];
}

export interface FlowsApiResponse {
    flows: FlowSummary[];
    reputationAnalytics?: {
        reputation_score?: number;
        deliverability_rate?: number;
    };
}

export interface CreateTemplateFlowResponse {
    flow_id: number;
}

export interface FlowAnalyticsResponse {
    analytics: Record<
        number,
        {
            analytics: FlowAnalytics;
            emails: Record<number, FlowAnalytics>;
        }
    >;
    has_more: boolean;
}

// Fan/Recipient structure
export interface Fan {
    fan_id: number;
    user_fan_id: number;
    name: string;
    email: string;
    subscription_status?: {
        name: string;
        timestamp?: number;
    };
}

// Recipient count response
export interface RecipientCountResponse {
    count: number;
    fans: Fan[];
}

// Fan tags list response
export interface FanTagsListResponse {
    tags: string[];
}

// Create/Update flow payload
export interface CreateFlowPayload {
    status: FlowStatus;
    type: FlowType;
    trigger: FlowTrigger;
    data: {
        name: string;
        filters?: {
            page_id_list?: number[];
            exclude_page_id_list?: number[];
        };
        specific_time?: {
            tags: Tag[];
            date: string;
            time: string;
            timezone: string;
        };
    };
}

export interface UpdateFlowPayload extends CreateFlowPayload {
    flow_id: number;
}

// Create flow response
export interface CreateFlowResponse {
    flow_id: number;
}

// Paginated flows response
export interface PaginatedFlowsResponse {
    flows: FlowSummary[];
    total: number;
    page: number;
    per_page: number;
    has_more: boolean;
    reputationAnalytics?: {
        reputation_score?: number;
        deliverability_rate?: number;
    };
}
